/**
 * Email Thread Component
 * Modern conversation view inspired by Outlook
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Collapse,
  Chip,
  Stack,
  Button,
  Tooltip,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

import { EmailData } from '../../services/emailService';

// Styled components following design framework
const ThreadContainer = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  backgroundColor: theme.palette.background.default
}));

const EmailCard = styled(Card)<{ isLatest?: boolean }>(({ theme, isLatest }) => ({
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 2,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: isLatest ? theme.shadows[3] : theme.shadows[1],
  transition: theme.transitions.create(['box-shadow', 'border-color']),
  '&:hover': {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.light
  }
}));

const EmailHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.grey[50]
}));

const EmailContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  '& .email-body': {
    lineHeight: 1.6,
    '& p': {
      margin: theme.spacing(1, 0)
    }
  }
}));

const SenderInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5)
}));

const EmailActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}));

const ThreadSummary = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.light + '10',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.primary.light}`
}));

interface EmailThreadProps {
  emails: EmailData[];
  onReply?: (emailId: string) => void;
  onForward?: (emailId: string) => void;
  onStar?: (emailId: string, starred: boolean) => void;
}

const EmailThread: React.FC<EmailThreadProps> = ({
  emails,
  onReply,
  onForward,
  onStar
}) => {
  const theme = useTheme();
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());

  // Expand the latest email by default
  useEffect(() => {
    if (emails.length > 0) {
      const latestEmail = emails[emails.length - 1];
      setExpandedEmails(new Set([latestEmail.id]));
    }
  }, [emails]);

  const toggleEmailExpansion = (emailId: string) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    
    if (isToday) {
      return d.toLocaleTimeString('de-DE', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getInitials = (email: string) => {
    return email.split('@')[0].substring(0, 2).toUpperCase();
  };

  const isExpanded = (emailId: string) => expandedEmails.has(emailId);
  const isLatestEmail = (index: number) => index === emails.length - 1;

  if (emails.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="body1" color="text.secondary">
          No emails in this thread
        </Typography>
      </Box>
    );
  }

  return (
    <ThreadContainer>
      {/* Thread Summary */}
      {emails.length > 1 && (
        <ThreadSummary>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                📧 Email Thread
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {emails.length} messages • {emails.filter(e => e.status === 'unread').length} unread
              </Typography>
            </Box>
            <Chip 
              label={`${emails.length} emails`}
              color="primary"
              size="small"
            />
          </Stack>
        </ThreadSummary>
      )}

      {/* Email List */}
      {emails.map((email, index) => {
        const expanded = isExpanded(email.id);
        const isLatest = isLatestEmail(index);

        return (
          <EmailCard key={email.id} isLatest={isLatest}>
            <EmailHeader>
              <SenderInfo>
                <Avatar
                  sx={{ 
                    width: 36, 
                    height: 36,
                    backgroundColor: theme.palette.primary.main,
                    fontSize: '0.875rem'
                  }}
                >
                  {getInitials(email.from_address)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {email.from_address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(email.email_date)} • To: {email.to_addresses.join(', ')}
                  </Typography>
                </Box>
                {email.attachments && email.attachments.length > 0 && (
                  <Tooltip title={`${email.attachments.length} attachments`}>
                    <AttachFileIcon fontSize="small" color="action" />
                  </Tooltip>
                )}
                {email.status === 'unread' && (
                  <Chip 
                    label="New"
                    color="primary"
                    size="small"
                    sx={{ fontSize: '0.75rem', height: 20 }}
                  />
                )}
              </SenderInfo>

              <EmailActions>
                <IconButton
                  size="small"
                  onClick={() => onStar?.(email.id, !email.is_starred)}
                  sx={{ color: email.is_starred ? 'warning.main' : 'action.active' }}
                >
                  {email.is_starred ? <StarIcon /> : <StarBorderIcon />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onReply?.(email.id)}
                >
                  <ReplyIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onForward?.(email.id)}
                >
                  <ForwardIcon />
                </IconButton>
                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => toggleEmailExpansion(email.id)}
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </EmailActions>
            </EmailHeader>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <EmailContent>
                {/* Subject (only show if different from thread) */}
                {index === 0 && (
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {email.subject}
                  </Typography>
                )}

                {/* Email Body */}
                <Box className="email-body">
                  {email.body_html ? (
                    <div 
                      dangerouslySetInnerHTML={{ __html: email.body_html }}
                      style={{ wordBreak: 'break-word' }}
                    />
                  ) : (
                    <Typography 
                      variant="body1" 
                      component="div"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {email.body_text}
                    </Typography>
                  )}
                </Box>

                {/* Attachments */}
                {email.attachments && email.attachments.length > 0 && (
                  <Box mt={2}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      📎 Attachments ({email.attachments.length})
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {email.attachments.map((attachment, idx) => (
                        <Chip
                          key={idx}
                          label={attachment.filename}
                          variant="outlined"
                          size="small"
                          icon={<AttachFileIcon />}
                          clickable
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Quick Actions */}
                {isLatest && (
                  <Box mt={3}>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        startIcon={<ReplyIcon />}
                        onClick={() => onReply?.(email.id)}
                        size="small"
                      >
                        Reply
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ForwardIcon />}
                        onClick={() => onForward?.(email.id)}
                        size="small"
                      >
                        Forward
                      </Button>
                    </Stack>
                  </Box>
                )}
              </EmailContent>
            </Collapse>

            {/* Collapsed Preview */}
            {!expanded && (
              <Box px={2} pb={2}>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {email.body_text?.substring(0, 100)}...
                </Typography>
              </Box>
            )}
          </EmailCard>
        );
      })}
    </ThreadContainer>
  );
};

export default EmailThread;
